/**
 * StockMovementSwitcher — the fixed door between Stock Requests and
 * Branch Transfers (owner 2026-09-19: "they keep moving places").
 *
 * IA law: the two are ONE area with TWO pages. Each page keeps its own
 * route, job and tabs; this strip sits in the same spot at the top of both,
 * always in flow order (request → transfer), and is the only way the pages
 * point at each other in their headers. It replaced the "Request Stock"
 * pseudo-tab on Branch Transfers — a control that looked like a tab but
 * navigated away.
 *
 * Counts come from the alert bubble's endpoint, so the switcher, the bubble
 * and the sidebar can never disagree about what is waiting.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight, ArrowRight, ClipboardList } from 'lucide-react';
import { useAuth, api } from '../contexts/AuthContext';

const POLL_INTERVAL_MS = 60_000;

const PAGES = [
  { key: 'requests',  to: '/stock-requests',   label: 'Stock Requests',   Icon: ClipboardList },
  { key: 'transfers', to: '/branch-transfers', label: 'Branch Transfers', Icon: ArrowRight },
];

function Count({ n, label }) {
  if (!n) return null;
  return (
    <span className="ml-1.5 inline-flex min-w-[18px] justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold leading-[18px] text-white"
          title={`${n} waiting for you`} aria-label={`${n} ${label} waiting for you`}>
      {n}
    </span>
  );
}

export default function StockMovementSwitcher({ current }) {
  const { currentBranch } = useAuth();
  const [waiting, setWaiting] = useState({ requests: 0, transfers: 0 });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (currentBranch?.id) params.branch_id = currentBranch.id;
      const { data } = await api.get('/branch-transfers/pending-alerts', { params });
      const requests = data?.new_flow_requests || 0;
      setWaiting({ requests, transfers: Math.max(0, (data?.total || 0) - requests) });
    } catch { /* counts are a courtesy — the switcher works without them */ }
  }, [currentBranch?.id]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    window.addEventListener('agribooks:alerts-refresh', load);
    return () => { clearInterval(id); window.removeEventListener('agribooks:alerts-refresh', load); };
  }, [load]);

  const other = PAGES.find(p => p.key !== current);

  return (
    <nav aria-label="Stock requests and branch transfers" data-testid="stock-movement-switcher">
      {/* Desktop: both pages, fixed order, the current one lit */}
      <div className="hidden sm:inline-flex rounded-lg border border-slate-200 bg-slate-100 p-0.5">
        {PAGES.map(({ key, to, label, Icon }) => (key === current ? (
          <span key={key} aria-current="page"
                className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-slate-900 shadow-sm"
                data-testid={`switcher-${key}`}>
            <Icon size={14} className="mr-1.5 text-[#1A4D2E]" /> {label}
            <Count n={waiting[key]} label={label.toLowerCase()} />
          </span>
        ) : (
          <Link key={key} to={to}
                className="inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900"
                data-testid={`switcher-${key}`}>
            <Icon size={14} className="mr-1.5" /> {label}
            <Count n={waiting[key]} label={label.toLowerCase()} />
          </Link>
        )))}
      </div>

      {/* Phone: one compact button to the OTHER page — no second tab row */}
      <Link to={other.to}
            className="sm:hidden inline-flex items-center rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700"
            data-testid="switcher-compact">
        <ArrowLeftRight size={13} className="mr-1.5 text-slate-500" /> {other.label}
        <Count n={waiting[other.key]} label={other.label.toLowerCase()} />
      </Link>
    </nav>
  );
}
